import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Divider, Paper, Stack, Typography,
} from '@mui/material';
import { ArrowBack, CheckCircle, Cancel, ErrorOutline } from '@mui/icons-material';
import ButtonOne from '../../../common/normal-button';
import { CTWA_PUBLISH_VALIDATE } from '../../../config/urls';
import { ColorsConstants } from '../../../cssUtils/constants';
import { useCtwaApi } from './api';
import { useChatbots } from './ChatSections';
import { GOALS, SPECIAL_AD_CATEGORIES } from './constants';
import { Note, currencySymbol } from './ui';

// Checklist rows; each covers one or more form sections so "Fix" opens the first of them.
const GROUPS = [
  { label: 'Account setup', sections: ['account'] },
  { label: 'Objective & category', sections: ['objective'] },
  { label: 'Placements', sections: ['placements'] },
  { label: 'Ads', sections: ['ads'] },
  { label: 'Targeting', sections: ['targeting'] },
  { label: 'Budget & schedule', sections: ['budget'] },
  { label: 'Autofill & chatbot', sections: ['autofill'] },
];

const ICON = {
  ok: <CheckCircle sx={{ color: '#166534' }} />,
  warning: <ErrorOutline sx={{ color: '#B45309' }} />,
  error: <Cancel sx={{ color: '#B91C1C' }} />,
};

const SummaryBlock = ({ title, lines }) => (
  <Box>
    <Typography variant="caption" fontWeight={700} color={ColorsConstants.primaryGray50} sx={{ letterSpacing: 0.5 }}>{title}</Typography>
    {lines.filter(Boolean).map((l, i) => <Typography key={i} variant="body2">{l}</Typography>)}
  </Box>
);

/**
 * localErrors: { sectionId: [messages] } from validation.js.
 * retiredNames: interests Meta retired (dropped from the payload, shown as a warning).
 */
const ReviewScreen = ({
  form, payload, localErrors, retiredNames, accountInfo, igConnected,
  onFix, onBack, onPublish, onSaveDraft, publishing, savingDraft, publishError, canPublish,
}) => {
  const api = useCtwaApi();
  const bots = useChatbots();
  const [remote, setRemote] = useState(null); // { issues: [{ section, severity, message }] }
  const [remoteError, setRemoteError] = useState('');
  const [checking, setChecking] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);

  const runRemoteChecks = async () => {
    setChecking(true);
    setRemoteError('');
    try {
      setRemote(await api.post(CTWA_PUBLISH_VALIDATE, payload));
      setCheckedAt(new Date());
    } catch (e) {
      setRemote(null);
      setRemoteError(e.message);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    // Meta is only asked once the local rules pass; otherwise its errors just repeat ours.
    if (Object.keys(localErrors).length === 0) runRemoteChecks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = GROUPS.map((g) => {
    const local = g.sections.flatMap((s) => localErrors[s] || []);
    const fromMeta = (remote?.issues || []).filter((i) => g.sections.includes(i.section));
    const errors = [...local, ...fromMeta.filter((i) => i.severity === 'error').map((i) => i.message)];
    const warnings = fromMeta.filter((i) => i.severity !== 'error').map((i) => i.message);
    if (g.sections.includes('targeting') && retiredNames.length) {
      warnings.push(`${retiredNames.join(', ')} ${retiredNames.length === 1 ? 'was' : 'were'} retired by Meta and will be removed.`);
    }
    if (g.sections.includes('account') && !igConnected) warnings.push('Instagram not linked – ads will run on Facebook only.');
    return { ...g, errors, warnings, status: errors.length ? 'error' : warnings.length ? 'warning' : 'ok' };
  });

  const blocking = rows.reduce((n, r) => n + r.errors.length, 0);
  const metaPending = checking || (!remote && Object.keys(localErrors).length === 0);
  const canClickPublish = canPublish && !blocking && !metaPending && !remoteError && !publishing;

  const sym = currencySymbol(accountInfo?.currency || 'INR');
  const goal = GOALS.find((g) => g.value === form.goal)?.label;
  const category = SPECIAL_AD_CATEGORIES.find((c) => c.value === form.specialAdCategory)?.label;
  const t = form.targeting;

  return (
    <Box sx={{ p: 3, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ textTransform: 'none', color: ColorsConstants.primaryGray60 }}>Back to edit</Button>
        <Typography variant="h6" fontWeight={700}>Review & publish: {form.name || 'Untitled campaign'}</Typography>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="flex-start">
        <Paper elevation={0} sx={{ flex: 3, width: '100%', p: 2.5, border: `1px solid ${ColorsConstants.primaryGray10}`, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="body1" fontWeight={700}>Checks</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              {checking && <CircularProgress size={14} />}
              <Note>{checking ? 'Checking with Meta…' : checkedAt ? `Checked with Meta at ${checkedAt.toLocaleTimeString()} (nothing created yet)` : ''}</Note>
              {!checking && Object.keys(localErrors).length === 0 && (
                <Button size="small" onClick={runRemoteChecks} sx={{ textTransform: 'none' }}>Check again</Button>
              )}
            </Stack>
          </Stack>
          {remoteError && (
            <Alert severity="error" sx={{ mb: 1 }}>Couldn't check with Meta: {remoteError}. Publishing stays off until this check passes.</Alert>
          )}
          {rows.map((r) => (
            <Stack key={r.label} direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.25, borderBottom: `1px solid ${ColorsConstants.primaryGray10}` }}>
              {ICON[r.status]}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600}>{r.label}</Typography>
                {[...r.errors, ...r.warnings].map((m, i) => (
                  <Typography key={i} variant="caption" sx={{ display: 'block', color: i < r.errors.length ? '#991B1B' : ColorsConstants.primaryGray50 }}>{m}</Typography>
                ))}
              </Box>
              {r.status !== 'ok' && (
                <Button size="small" onClick={() => onFix(r.sections.find((s) => (localErrors[s] || []).length) || r.sections[0])} sx={{ textTransform: 'none' }}>Fix</Button>
              )}
            </Stack>
          ))}
        </Paper>

        <Stack spacing={2} sx={{ flex: 2, width: '100%' }}>
          <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${ColorsConstants.primaryGray10}`, borderRadius: 2 }}>
            <Stack spacing={1.5} divider={<Divider />}>
              <SummaryBlock title="CAMPAIGN" lines={[goal, `Special ad category: ${category}`, `${form.account.adAccountName || form.account.adAccountId} · ${form.account.pageName || form.account.pageId}`]} />
              <SummaryBlock title="AD SET" lines={[
                `${sym}${form.budget.daily || '—'}/day · ${form.budget.startDate ? `from ${form.budget.startDate}` : 'starts on approval'} · ${form.budget.endMode === 'date' ? `until ${form.budget.endDate}` : 'no end date'}`,
                `${t.locations.map((l) => l.name).join(', ') || 'No location'} · ${t.ageMin}–${t.ageMax >= 65 ? '65+' : t.ageMax}`,
                `${t.advantageAudience ? 'Advantage+ audience' : 'Exact audience'} · ${form.placementMode === 'automatic' ? 'Automatic placements' : `${form.placements.length} placements`}`,
                `Chats go to +${form.account.waNumber || '—'}`,
              ]} />
              <SummaryBlock title={`ADS (${form.ads.length})`} lines={form.ads.map((a, i) => {
                const bot = form.chatbotMode === 'all' ? form.chatbotAll : a.chatbot;
                return `Ad ${i + 1} · ${a.name} · ${a.format.toLowerCase()} · ${bot ? `chatbot: ${bots.nameOf(bot)}` : 'no chatbot'}`;
              })} />
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ p: 2.5, border: `1px solid ${ColorsConstants.primaryGray10}`, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>What happens when you publish</Typography>
            <Note>We create the campaign, ad set and ads paused, then switch them on together. If any step fails, nothing goes live and you are not charged.</Note>
            <Note sx={{ mt: 0.5 }}>Meta reviews new ads, usually within a day. You'll see the status on the campaign page right after publishing.</Note>
            {publishError && <Alert severity="error" sx={{ mt: 1.5 }}>{publishError}</Alert>}
            <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
              <ButtonOne title={savingDraft ? 'Saving…' : 'Save as Draft'} variant="outlined" onClick={onSaveDraft} disabled={savingDraft || publishing} />
              <ButtonOne
                title={publishing ? 'Publishing…' : blocking ? `Fix ${blocking} ${blocking === 1 ? 'issue' : 'issues'} to publish` : `Publish ${form.ads.length} ads`}
                onClick={onPublish} disabled={!canClickPublish}
                startIcon={publishing && <CircularProgress size={16} color="inherit" />}
              />
            </Stack>
            {!canPublish && <Note sx={{ mt: 1 }}>You don't have permission to publish campaigns.</Note>}
          </Paper>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ReviewScreen;
